import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';

export interface AlertProps {
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    action?: React.ReactNode;
    onClose?: () => void;
    autoClose?: boolean;
    autoCloseDelay?: number;
}

/**
 * Alert component for displaying API messages
 */
export const Alert: React.FC<AlertProps> = ({
    type,
    message,
    action,
    onClose,
    autoClose = false,
    autoCloseDelay = 3000
}) => {
    const theme = useTheme();

    useEffect(() => {
        if (autoClose) {
            // Call onClose when progress bar completes, which triggers exit animation
            const removeTimer = setTimeout(() => {
                onClose?.();
            }, autoCloseDelay); // Remove when progress bar finishes

            return () => {
                clearTimeout(removeTimer);
            };
        }
    }, [autoClose, autoCloseDelay, onClose]);

    const handleClose = () => {
        onClose?.();
    };

    const getAlertColors = () => {
        switch (type) {
            case 'success':
                return {
                    backgroundColor: '#d1fae5',
                    border: '1px solid #10b981',
                    color: '#065f46',
                };
            case 'error':
                return {
                    backgroundColor: '#fee2e2',
                    border: '1px solid #ef4444',
                    color: '#991b1b',
                };
            case 'warning':
                return {
                    backgroundColor: '#fef3c7',
                    border: '1px solid #f59e0b',
                    color: '#92400e',
                };
            case 'info':
                return {
                    backgroundColor: '#dbeafe',
                    border: '1px solid #3b82f6',
                    color: '#1e40af',
                };
            default:
                return {
                    backgroundColor: theme.colors.surface,
                    border: `1px solid ${theme.colors.border.default}`,
                    color: theme.colors.text.primary,
                };
        }
    };

    const alertColors = getAlertColors();

    const getIcon = () => {
        switch (type) {
            case 'success':
                return '✓';
            case 'error':
                return '✕';
            case 'warning':
                return '⚠';
            case 'info':
                return 'ℹ';
            default:
                return '';
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{
                opacity: 0,
                x: 100,
                transition: {
                    duration: 0.3,
                    ease: [0.4, 0, 0.2, 1],
                },
            }}
            transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1],
            }}
            style={{
                ...alertColors,
                position: 'relative',
                padding: 0,
                overflow: 'hidden',
                borderRadius: theme.borderRadius.md,
                marginBottom: theme.spacing.sm,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '100%',
                wordWrap: 'break-word' as const,
            }}
        >
            <div style={{
                padding: theme.spacing.md,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flex: 1 }}>
                    <span style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        flexShrink: 0
                    }}>
                        {getIcon()}
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs, flex: 1 }}>
                        <span style={{
                            fontSize: theme.typography.fontSize.sm,
                            lineHeight: 1.4
                        }}>
                            {message}
                        </span>
                        {action && (
                            <div style={{ marginTop: theme.spacing.xs }}>
                                {action}
                            </div>
                        )}
                    </div>
                </div>
                <button
                    onClick={handleClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '18px',
                        cursor: 'pointer',
                        color: 'inherit',
                        opacity: 0.7,
                        padding: '0',
                        marginLeft: theme.spacing.sm,
                        flexShrink: 0,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                >
                    ×
                </button>
            </div>
            {autoClose && (
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '3px',
                    backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    width: '100%',
                }}>
                    <motion.div
                        key={`progress-${autoCloseDelay}`}
                        initial={{ width: '0%' }}
                        animate={{ width: '100%' }}
                        transition={{
                            duration: autoCloseDelay / 1000,
                            ease: 'linear',
                        }}
                        style={{
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        }}
                    />
                </div>
            )}
        </motion.div>
    );
};

/**
 * Hook for managing alerts
 */
export const useAlert = () => {
    const [alerts, setAlerts] = useState<Array<{
        id: string;
        type: 'success' | 'error' | 'warning' | 'info';
        message: string;
    }>>([]);
    const alertCounterRef = React.useRef(0);

    const addAlert = (
        type: 'success' | 'error' | 'warning' | 'info',
        message: string,
        autoClose: boolean = type === 'success'
    ) => {
        // Use timestamp + counter + random to ensure unique IDs even if alerts are created in the same millisecond
        alertCounterRef.current += 1;
        const id = `${Date.now()}-${alertCounterRef.current}-${Math.random().toString(36).substr(2, 9)}`;
        setAlerts(prev => [...prev, { id, type, message }]);

        if (autoClose) {
            setTimeout(() => {
                removeAlert(id);
            }, 3000);
        }
    };

    const removeAlert = (id: string) => {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
    };

    const clearAll = () => {
        setAlerts([]);
    };

    return {
        alerts,
        addAlert,
        removeAlert,
        clearAll,
    };
};
